(() => {
  if (window.spiforaDrawerLoaded) return;
  window.spiforaDrawerLoaded = true;
  const drawer = document.querySelector('cart-drawer.sf-cart-drawer');
  if (!drawer) return;
  let readController;
  let mutationQueue = Promise.resolve();
  let mutationPending = false;
  let reconcilePromise;
  let reconcileDirty = false;
  let openRequested = false;
  let publishingOwnUpdate = false;
  let lastCartState;
  let nativeOpen;
  let pendingJobs = 0;
  let reconciliationSafe = true;
  const checkoutInitialDisabled = new WeakMap();
  const deniedGiftCarts = new Set();
  const nativeError = () => window.cartStrings?.error || 'Unable to update your cart. Please try again.';
  const errorMessage = (value) => typeof value === 'string' ? value : Object.values(value || {}).flat().join(' ');
  const route = (name, fallback) => window.routes?.[name] || `${window.Shopify?.routes?.root || '/'}${fallback}`;
  const cartUrl = () => route('cart_url', 'cart');
  const readConfig = () => {
    try {
      const data = JSON.parse(drawer.dataset.sfGiftConfig || '{}');
      return {
        enabled: data.enabled === true,
        currency: String(data.currency || 'INR'),
        eligibleProductIds: Array.isArray(data.eligibleProductIds) ? data.eligibleProductIds.map(String) : [],
        gifts: Array.isArray(data.gifts) ? data.gifts.filter((gift) => /^\d+$/.test(String(gift.variantId)) && Number.isSafeInteger(Number(gift.minimum)) && Number(gift.minimum) > 0).map((gift) => ({ variantId: String(gift.variantId), minimum: Number(gift.minimum) })) : [],
      };
    } catch (_) {
      return { enabled: false, currency: 'INR', eligibleProductIds: [], gifts: [] };
    }
  };
  function planGifts(cart, config, blocked) {
    const giftIds = new Set(config.gifts.map((gift) => String(gift.variantId)));
    const eligibleIds = new Set(config.eligibleProductIds.map(String));
    const paid = (cart.items || []).filter((item) => !giftIds.has(String(item.variant_id)));
    const subtotal = paid.reduce((sum, item) => sum + (item.requires_shipping && eligibleIds.has(String(item.product_id)) ? Math.max(0, Number(item.final_line_price) || 0) : 0), 0);
    const paidCount = paid.reduce((sum, item) => sum + Math.max(0, Number(item.quantity) || 0), 0);
    const fingerprint = `${cart.currency}:${paid.map((item) => JSON.stringify([item.product_id, item.variant_id, item.selling_plan_allocation?.selling_plan?.id || null, item.quantity, item.final_line_price, item.requires_shipping === true, Object.entries(item.properties || {}).sort(([left], [right]) => left.localeCompare(right))])).sort().join('|')}`;
    const updates = {};
    const additions = [];
    const unpaid = [];
    for (const gift of config.gifts) {
      const id = String(gift.variantId);
      const lines = (cart.items || []).filter((item) => String(item.variant_id) === id);
      const wanted = config.enabled && cart.currency === config.currency && subtotal >= gift.minimum && !blocked.has(`${id}|${fingerprint}`);
      if (!wanted) {
        lines.forEach((item) => { updates[item.key] = 0; });
      } else if (!lines.length) {
        additions.push({ id: Number(id), quantity: 1 });
      } else {
        if (lines[0].quantity !== 1) updates[lines[0].key] = 1;
        lines.slice(1).forEach((item) => { updates[item.key] = 0; });
        if (lines.length === 1 && lines[0].quantity === 1 && Number(lines[0].final_line_price) > 0) unpaid.push(id);
      }
    }
    return { subtotal, paidCount, fingerprint, updates, additions, unpaid };
  }
  const readCart = async () => {
    const url = new URL(`${window.Shopify?.routes?.root || '/'}cart.js`, location.origin);
    const response = await fetch(url, { credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error(nativeError());
    return response.json();
  };
  const postCart = async (url, body) => {
    const response = await fetch(url, {
      method: 'POST', credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify(body),
    });
    const state = await response.json();
    if (!response.ok || state.errors) throw new Error(errorMessage(state.errors || state.description) || nativeError());
    return state;
  };
  const reconcileGifts = async () => {
    const config = readConfig();
    let cart = await readCart();
    if (!config.gifts.length) return { cart, changed: false, denied: false };
    let changed = false;
    let denied = false;
    for (let pass = 0; pass < 6; pass += 1) {
      let plan = planGifts(cart, config, deniedGiftCarts);
      if (!Object.keys(plan.updates).length && !plan.additions.length && plan.unpaid.length) {
        plan.unpaid.forEach((id) => deniedGiftCarts.add(`${id}|${plan.fingerprint}`));
        denied = true;
        plan = planGifts(cart, config, deniedGiftCarts);
      }
      if (!Object.keys(plan.updates).length && !plan.additions.length) return { cart, changed, denied };
      if (Object.keys(plan.updates).length) {
        await postCart(route('cart_update_url', 'cart/update.js'), { updates: plan.updates });
      } else {
        try {
          await postCart(route('cart_add_url', 'cart/add.js'), { items: plan.additions });
        } catch (error) {
          plan.additions.forEach((item) => deniedGiftCarts.add(`${item.id}|${plan.fingerprint}`));
          denied = true;
          cart = await readCart();
          changed = true;
          continue;
        }
      }
      changed = true;
      cart = await readCart();
    }
    throw new Error(nativeError());
  };
  const setError = (message, line) => {
    const rowError = line && drawer.querySelector(`#CartDrawer-LineItemError-${line} .cart-item__error-text`);
    const target = rowError || drawer.querySelector('#CartDrawer-CartErrors');
    if (target) target.textContent = message;
  };
  const gateCheckout = () => {
    const locked = pendingJobs > 0 || !reconciliationSafe;
    drawer.querySelectorAll('#CartDrawer-Checkout').forEach((button) => {
      if (locked) {
        if (!checkoutInitialDisabled.has(button)) checkoutInitialDisabled.set(button, button.disabled);
        button.disabled = true;
        button.setAttribute('aria-busy', pendingJobs > 0 ? 'true' : 'false');
      } else if (checkoutInitialDisabled.has(button)) {
        button.disabled = checkoutInitialDisabled.get(button);
        checkoutInitialDisabled.delete(button);
        button.removeAttribute('aria-busy');
      }
    });
  };
  const bindProductForms = () => {
    document.querySelectorAll('product-form').forEach((form) => {
      if (typeof form.onSubmitHandler !== 'function') return;
      form.cart = drawer;
      form.querySelector('[type="submit"]')?.setAttribute('aria-haspopup', 'dialog');
    });
  };
  const syncState = () => {
    const items = drawer.querySelector('cart-drawer-items');
    const empty = !items || items.classList.contains('is-empty');
    drawer.classList.toggle('is-empty', empty);
    const badge = drawer.querySelector('.sf-drawer-count');
    const count = lastCartState ? planGifts(lastCartState, readConfig(), deniedGiftCarts).paidCount : Number(badge?.textContent.trim() || drawer.dataset.sfPaidItemCount || 0);
    if (badge) badge.textContent = String(count);
    drawer.dataset.sfPaidItemCount = String(count);
    const bubble = document.getElementById('cart-icon-bubble');
    if (bubble) {
      bubble.querySelectorAll('.sf-bag-count, .cart-count-bubble span[aria-hidden="true"]').forEach((counter) => { counter.textContent = String(count); });
      bubble.dataset.sfCartLabel ||= (bubble.getAttribute('aria-label') || '').replace(/\s*\(\d+\)\s*$/, '');
      if (bubble.dataset.sfCartLabel) bubble.setAttribute('aria-label', `${bubble.dataset.sfCartLabel} (${count})`);
    }
    document.getElementById('main-cart-footer')?.classList.toggle('is-empty', empty);
    document.querySelector('#MainContent cart-items')?.classList.toggle('is-empty', empty);
    bindProductForms();
    gateCheckout();
  };
  const pageSections = () => ['main-cart-items', 'main-cart-footer'].flatMap((id) => {
    const element = document.getElementById(id);
    return element?.dataset.id ? [{ id, section: element.dataset.id, selector: '.js-contents' }] : [];
  });
  const replaceSections = (sections, openAfter = false, pageParts = []) => {
    if (typeof sections?.['cart-drawer'] !== 'string') throw new Error(nativeError());
    const source = new DOMParser().parseFromString(sections['cart-drawer'], 'text/html');
    const incoming = source.getElementById('CartDrawer');
    if (!incoming?.querySelector('.sf-drawer-scroll')) throw new Error(nativeError());
    const active = document.activeElement;
    const restoreId = drawer.contains(active) ? active.id : null;
    const wasOpen = drawer.classList.contains('active');
    const scroll = drawer.querySelector('.sf-drawer-scroll')?.scrollTop || 0;
    drawer.querySelector('#CartDrawer').innerHTML = incoming.innerHTML;
    if (typeof sections['cart-icon-bubble'] === 'string') {
      const iconSource = new DOMParser().parseFromString(sections['cart-icon-bubble'], 'text/html');
      const icon = iconSource.querySelector('.shopify-section');
      if (icon && document.getElementById('cart-icon-bubble')) document.getElementById('cart-icon-bubble').innerHTML = icon.innerHTML;
    }
    pageParts.forEach(({ id, section, selector }) => {
      const target = document.getElementById(id)?.querySelector(selector);
      if (!target || typeof sections[section] !== 'string') return;
      const fragment = new DOMParser().parseFromString(sections[section], 'text/html').querySelector(selector);
      if (fragment) target.innerHTML = fragment.innerHTML;
    });
    syncState();
    const scroller = drawer.querySelector('.sf-drawer-scroll');
    if (scroller) scroller.scrollTop = scroll;
    if (openAfter) nativeOpen?.();
    else if (wasOpen && typeof trapFocus === 'function') {
      const focus = (restoreId && drawer.querySelector(`#${CSS.escape(restoreId)}`)) || drawer.querySelector('.drawer__close');
      trapFocus(drawer.querySelector('.drawer__inner'), focus);
    }
  };
  const renderFresh = async (openAfter = false) => {
    readController?.abort();
    readController = new AbortController();
    const parts = pageSections();
    const url = new URL(cartUrl(), location.origin);
    url.searchParams.set('sections', ['cart-drawer', 'cart-icon-bubble', ...parts.map((part) => part.section)].join(','));
    const response = await fetch(url, { credentials: 'same-origin', cache: 'no-store', signal: readController.signal });
    if (!response.ok) throw new Error(nativeError());
    replaceSections(await response.json(), openAfter, parts);
  };
  const publishOwnUpdate = (cart, variantId) => {
    if (typeof publish !== 'function') return;
    publishingOwnUpdate = true;
    try { publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: cart, variantId }); }
    finally { publishingOwnUpdate = false; }
  };
  const enqueue = (work) => {
    pendingJobs += 1;
    gateCheckout();
    mutationQueue = mutationQueue.catch(() => {}).then(async () => {
      mutationPending = true;
      readController?.abort();
      drawer.setAttribute('aria-busy', 'true');
      try { return await work(); }
      finally {
        drawer.removeAttribute('aria-busy');
        mutationPending = false;
        pendingJobs -= 1;
        gateCheckout();
      }
    });
    return mutationQueue;
  };
  const settleAndRender = async (openAfter = false) => {
    let outcome;
    let problem;
    reconciliationSafe = false;
    try {
      outcome = await reconcileGifts();
      lastCartState = outcome.cart;
    } catch (error) {
      problem = error;
      lastCartState = await readCart().catch(() => undefined);
    }
    await renderFresh(openAfter);
    reconciliationSafe = !problem;
    if (problem || outcome?.denied) setError(problem?.message || nativeError());
    else setError('');
    if (outcome?.changed) publishOwnUpdate(outcome.cart);
    return lastCartState;
  };
  const refresh = (openAfter = false) => {
    openRequested ||= openAfter;
    if (reconcilePromise) {
      reconcileDirty = true;
      return reconcilePromise;
    }
    reconcilePromise = enqueue(async () => {
      let rounds = 0;
      do {
        reconcileDirty = false;
        const shouldOpen = openRequested;
        openRequested = false;
        try { await settleAndRender(shouldOpen); }
        catch (error) { if (error.name !== 'AbortError') setError(nativeError()); }
        rounds += 1;
      } while (reconcileDirty && rounds < 2);
    }).finally(() => {
      reconcilePromise = undefined;
      if (reconcileDirty) refresh(false);
    });
    return reconcilePromise;
  };
  const updateQuantity = function (line, quantity, event, name, variantId) {
    const input = this.querySelector(`#Drawer-quantity-${line}`);
    const key = input?.dataset.lineKey;
    if (!key || !Number.isInteger(Number(quantity)) || Number(quantity) < 0) return;
    const desiredQuantity = Number(quantity);
    return enqueue(async () => {
      setError('', line);
      const row = drawer.querySelector(`[data-line-key="${CSS.escape(key)}"]`)?.closest('.cart-item');
      const controls = [...drawer.querySelectorAll('.quantity__button, .quantity__input, cart-remove-button button')];
      const previousDisabled = controls.map((control) => control.disabled);
      controls.forEach((control) => { control.disabled = true; });
      row?.querySelectorAll('.loading__spinner').forEach((spinner) => spinner.classList.remove('hidden'));
      try {
        await postCart(route('cart_change_url', 'cart/change.js'), { id: key, quantity: desiredQuantity });
        const state = await settleAndRender(false);
        if (state) publishOwnUpdate(state, variantId);
        const updated = drawer.querySelector(`[data-line-key="${CSS.escape(key)}"]`);
        const focus = name && updated?.closest('.cart-item')?.querySelector(`[name="${CSS.escape(name)}"]`);
        focus?.focus();
      } catch (error) {
        if (input?.isConnected) input.value = input.getAttribute('value');
        setError(error.message || nativeError(), line);
      } finally {
        controls.forEach((control, index) => { if (control.isConnected) control.disabled = previousDisabled[index]; });
        drawer.querySelectorAll('.loading__spinner').forEach((spinner) => spinner.classList.add('hidden'));
      }
    });
  };
  Promise.all([customElements.whenDefined('cart-drawer'), customElements.whenDefined('cart-drawer-items')]).then(() => {
    const prototype = customElements.get('cart-drawer-items').prototype;
    const previousUpdate = prototype.updateQuantity;
    const previousChange = prototype.onChange;
    const previousRefresh = prototype.onCartUpdate;
    prototype.updateQuantity = function (...args) {
      return this.closest('.sf-cart-drawer') ? updateQuantity.apply(this, args) : previousUpdate.apply(this, args);
    };
    prototype.onChange = function (event) {
      if (!this.closest('.sf-cart-drawer')) return previousChange.call(this, event);
      const input = event.target;
      if (!input.matches('.quantity__input[data-index]')) return;
      const value = Number(input.value);
      const minimum = Number(input.dataset.min || 0);
      const maximum = input.max === '' ? Infinity : Number(input.max);
      const increment = Number(input.step || 1);
      if (!Number.isInteger(value) || value < 0 || value > maximum || (value !== 0 && (value < minimum || value % increment !== 0))) {
        input.setCustomValidity(nativeError());
        input.reportValidity();
        input.value = input.getAttribute('value');
        return;
      }
      input.setCustomValidity('');
      this.updateQuantity(input.dataset.index, value, event, input.name, input.dataset.quantityVariantId);
    };
    prototype.onCartUpdate = function () {
      if (!this.closest('.sf-cart-drawer')) return previousRefresh.call(this);
    };
    nativeOpen = drawer.open.bind(drawer);
    drawer.open = (trigger) => {
      nativeOpen(trigger);
      refresh(false);
    };
    const originalClose = drawer.close.bind(drawer);
    drawer.close = () => {
      if (!drawer.activeElement?.isConnected) drawer.activeElement = document.getElementById('cart-icon-bubble');
      originalClose();
    };
    if (typeof subscribe === 'function') subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (publishingOwnUpdate || event.source === 'product-form') return;
      if (mutationPending) reconcileDirty = true;
      refresh(false);
    });
    drawer.renderContents = () => {
      nativeOpen();
      return refresh(false);
    };
    drawer.addEventListener('click', (event) => {
      if (event.target.id === 'CartDrawer-Overlay') drawer.close();
    });
    document.addEventListener('click', (event) => {
      if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const link = event.target.closest('a[href]');
      if (!link || link.target === '_blank' || link.hasAttribute('download')) return;
      const url = new URL(link.href, location.href);
      const cart = new URL(cartUrl(), location.origin);
      if (url.origin !== location.origin || url.pathname.replace(/\/$/, '') !== cart.pathname.replace(/\/$/, '') || url.search || url.hash) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      drawer.open(link);
    }, true);
    new MutationObserver(() => syncState()).observe(drawer.querySelector('#CartDrawer'), { childList: true });
    customElements.whenDefined('product-form').then(bindProductForms);
    syncState();
  });
})();
